import { t, type StringKey } from '../../i18n'

/** Turning a backend refusal into something a cashier can act on.
 *
 * §13.8 sends stable CODES and §16.5 makes the client own the words: "backend messages
 * use stable codes and structured values; clients own translated user-facing text". So
 * this table is the client's half of that contract, and it is the only place a code
 * becomes a sentence.
 *
 * Every entry answers the cashier's real question, which is never "what went wrong" but
 * "what do I do now". A message that only names the fault leaves someone standing at a
 * till with a customer waiting.
 */
export type ErrorCode =
  // §13.8, the shared vocabulary.
  | 'UPGRADE_REQUIRED'
  | 'POINTS_SYSTEM_UNAVAILABLE'
  | 'LOCATION_LATE'
  | 'LOCATION_BLOCKED'
  | 'LOCATION_NOT_READY'
  | 'PRICE_CHANGED'
  | 'REWARD_UNAVAILABLE'
  | 'INSUFFICIENT_POINTS'
  | 'INVALID_QR_TYPE'
  | 'STALE_VERSION'
  | 'CLAIM_ALREADY_SETTLED'
  | 'CLAIM_EXPIRED'
  | 'CLAIM_REQUIRES_MANUAL_FULFILMENT'
  | 'REFERENCE_NOT_READY'
  // Client-side, from the designer's Errors/Messages note: "Can't use more than one at
  // a time (this is an optional option in the back-office)". The server never sends
  // this because the second reward is simply never offered.
  | 'DISCOUNT_ALREADY_APPLIED'
  // Also client-side. A 401 arrives with no envelope -- the surface refuses before it
  // parses anything -- so `callPos` mints this code itself. It was missing from this
  // table, which is how the single most likely failure on a freshly installed till came
  // out as "something went wrong" with nothing under it.
  | 'POS_CREDENTIAL_REJECTED'

interface ErrorCopy {
  title: StringKey
  body: StringKey
}

const COPY: Record<ErrorCode, ErrorCopy> = {
  DISCOUNT_ALREADY_APPLIED: {
    title: 'v3ErrDiscountAppliedTitle',
    body: 'v3ErrDiscountAppliedBody',
  },
  INSUFFICIENT_POINTS: { title: 'v3ErrLowBalanceTitle', body: 'v3ErrLowBalanceBody' },
  PRICE_CHANGED: { title: 'v3ErrPriceChangedTitle', body: 'v3ErrPriceChangedBody' },
  REWARD_UNAVAILABLE: { title: 'v3ErrRewardGoneTitle', body: 'v3ErrRewardGoneBody' },
  CLAIM_EXPIRED: { title: 'v3ErrClaimExpiredTitle', body: 'v3ErrClaimExpiredBody' },
  CLAIM_ALREADY_SETTLED: { title: 'v3ErrAlreadySettledTitle', body: 'v3ErrAlreadySettledBody' },
  CLAIM_REQUIRES_MANUAL_FULFILMENT: {
    title: 'v3ErrManualTitle',
    body: 'v3ErrManualBody',
  },
  POINTS_SYSTEM_UNAVAILABLE: { title: 'v3ErrOutageTitle', body: 'v3ErrOutageBody' },
  LOCATION_BLOCKED: { title: 'v3BillingBlockedTitle', body: 'v3BillingBlockedBody' },
  LOCATION_NOT_READY: { title: 'v3ErrNotReadyTitle', body: 'v3ErrNotReadyBody' },
  LOCATION_LATE: { title: 'v3ErrLateTitle', body: 'v3BillingLateWarning' },
  REFERENCE_NOT_READY: { title: 'v3ErrNotReadyTitle', body: 'v3ErrNotReadyBody' },
  STALE_VERSION: { title: 'v3ErrStaleTitle', body: 'v3ErrStaleBody' },
  INVALID_QR_TYPE: { title: 'v3ErrQrTitle', body: 'v3ErrQrBody' },
  UPGRADE_REQUIRED: { title: 'v3ErrUpgradeTitle', body: 'v3ErrUpgradeBody' },
  POS_CREDENTIAL_REJECTED: { title: 'v3ErrTillAuthTitle', body: 'v3ErrTillAuthBody' },
}

export interface ErrorMessage {
  title: string
  body: string
  /** Unknown codes are shown as-is so an operator can report something specific. A
   * generic "something went wrong" costs the support call its only useful detail. */
  code?: string
}

export function messageForCode(code: string | null | undefined): ErrorMessage {
  const copy = code ? COPY[code as ErrorCode] : undefined
  if (!copy) {
    // Two different fallbacks, because the copy has to match what is actually on screen.
    // "Tell support the code below" above an empty space is worse than saying nothing:
    // it asks the cashier for the one detail the app just failed to produce, and they
    // reach support with nothing. Only promise the code when there is one.
    return {
      title: t('v3ErrUnknownTitle'),
      body: code ? t('v3ErrUnknownBody') : t('v3ErrUnknownBodyNoCode'),
      code: code ?? undefined,
    }
  }
  return { title: t(copy.title), body: t(copy.body) }
}

/** Does this refusal leave the cashier a way forward inside the app?
 *
 * `CLAIM_REQUIRES_MANUAL_FULFILMENT` is the case §9.3 step 7 exists for: the automatic
 * discount failed, but the reward can still be handed over by hand and recorded. Those
 * dialogs get a second button; the rest only get "OK".
 */
export function offersManualFulfilment(code: string | null | undefined): boolean {
  return code === 'CLAIM_REQUIRES_MANUAL_FULFILMENT'
}
